import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  type ScheduleItem,
  type ScheduleSettings,
  type HolidayInfo,
  calculateEndDate,
  formatDateLabel,
  getPhaseColor,
} from '@/lib/scheduleUtils';
import { type BuildertrendRow } from '@/lib/buildertrendSeed';
import { supabase } from '@/lib/supabase';

export interface ImportPreviewRow {
  pdfRow: BuildertrendRow;
  matchedItem: ScheduleItem | null;
  newStartDate: string;
  newEndDate: string;
  newDuration: number;
  newColor: string;
  confidence: 'exact' | 'high' | 'medium' | 'low' | 'none';
  selected: boolean;
  /** True if there are meaningful changes vs the matched item */
  hasChanges: boolean;
}

export function useBuildertrendImport(
  projectId: string,
  existingItems: ScheduleItem[],
  pdfRows: BuildertrendRow[],
  settings: ScheduleSettings,
  holidays: HolidayInfo[]
) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // ── Fuzzy title matching ──
  const matchedRows = useMemo((): ImportPreviewRow[] => {
    return pdfRows.map((pdfRow) => {
      const { matchedItem, confidence } = findBestMatch(pdfRow, existingItems);

      const newStartDate = pdfRow.startDate;
      const newDuration = pdfRow.workdays;
      const newEndDate = calculateEndDate(newStartDate, newDuration, settings, holidays);
      const newColor = pdfRow.color;

      const hasChanges = matchedItem
        ? matchedItem.start_date !== newStartDate ||
          matchedItem.duration_days !== newDuration ||
          matchedItem.end_date !== newEndDate ||
          (matchedItem.bar_color_mode === 'custom' && matchedItem.bar_color !== newColor) ||
          matchedItem.phase !== pdfRow.phase
        : true;

      return {
        pdfRow,
        matchedItem,
        newStartDate,
        newEndDate,
        newDuration,
        newColor,
        confidence,
        selected: true, // default all selected
        hasChanges,
      };
    });
  }, [pdfRows, existingItems, settings, holidays]);

  const [rows, setRows] = useState<ImportPreviewRow[]>(() => matchedRows);

  // Update rows when matchedRows changes (e.g. items reload)
  useEffect(() => {
    setRows(matchedRows);
  }, [matchedRows]);

  const stats = useMemo(() => {
    const total = rows.length;
    const selected = rows.filter((r) => r.selected);
    const withChanges = selected.filter((r) => r.hasChanges);
    const exact = selected.filter((r) => r.confidence === 'exact').length;
    const high = selected.filter((r) => r.confidence === 'high').length;
    const medium = selected.filter((r) => r.confidence === 'medium').length;
    const low = selected.filter((r) => r.confidence === 'low').length;
    const none = selected.filter((r) => r.confidence === 'none').length;
    return { total, selectedCount: selected.length, withChangesCount: withChanges.length, exact, high, medium, low, none };
  }, [rows]);

  const toggleRow = useCallback((rowNo: number) => {
    setRows((prev) =>
      prev.map((r) => (r.pdfRow.rowNo === rowNo ? { ...r, selected: !r.selected } : r))
    );
  }, []);

  const selectAll = useCallback(() => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: true })));
  }, []);

  const deselectAll = useCallback(() => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: false })));
  }, []);

  const clearMessages = useCallback(() => {
    setSaveError('');
    setSaveSuccess('');
  }, []);

  // ── Save selected rows ──
  const saveSelected = useCallback(async () => {
    setSaving(true);
    clearMessages();
    const toSave = rows.filter((r) => r.selected);
    let updated = 0;
    let created = 0;
    const errors: string[] = [];

    try {
      for (const row of toSave) {
        if (row.matchedItem) {
          // Update existing
          const { error: updErr } = await supabase
            .from('jphph_project_schedule_items')
            .update({
              start_date: row.newStartDate,
              end_date: row.newEndDate,
              duration_days: row.newDuration,
              phase: row.pdfRow.phase,
              bar_color: row.newColor,
              bar_color_mode: 'custom',
              gantt_color: row.newColor,
              updated_at: new Date().toISOString(),
            })
            .eq('id', row.matchedItem.id);
          if (updErr) {
            errors.push(`${row.pdfRow.title}: ${updErr.message}`);
          } else {
            updated++;
          }
        } else {
          // Insert new item at end
          const maxSort = existingItems.reduce((m, i) => Math.max(m, i.sort_order), 0);
          const { error: insErr } = await supabase
            .from('jphph_project_schedule_items')
            .insert({
              project_id: projectId,
              title: row.pdfRow.title,
              phase: row.pdfRow.phase,
              duration_days: row.newDuration,
              start_date: row.newStartDate,
              end_date: row.newEndDate,
              progress: 0,
              is_complete: false,
              is_included: true,
              sort_order: maxSort + row.pdfRow.rowNo,
              bar_color: row.newColor,
              bar_color_mode: 'custom',
              gantt_color: row.newColor,
            });
          if (insErr) {
            errors.push(`${row.pdfRow.title}: ${insErr.message}`);
          } else {
            created++;
          }
        }
      }

      if (errors.length > 0) {
        setSaveError(`${errors.length} of ${toSave.length} items failed. First: ${errors[0]}`);
      } else {
        setSaveSuccess(`Imported ${updated} updated, ${created} new items from Buildertrend PDF.`);
      }
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Import save failed');
    } finally {
      setSaving(false);
    }
  }, [rows, existingItems, projectId, clearMessages]);

  return {
    rows,
    setRows,
    stats,
    toggleRow,
    selectAll,
    deselectAll,
    saveSelected,
    saving,
    saveError,
    saveSuccess,
    clearMessages,
  };
}

// ── Fuzzy matching ──────────────────────────────────────────

function findBestMatch(
  pdfRow: BuildertrendRow,
  existingItems: ScheduleItem[]
): { matchedItem: ScheduleItem | null; confidence: ImportPreviewRow['confidence'] } {
  if (existingItems.length === 0) return { matchedItem: null, confidence: 'none' };

  let best: { item: ScheduleItem | null; score: number } = { item: null, score: 0 };

  for (const item of existingItems) {
    const score = computeMatchScore(pdfRow.title, item.title);
    if (score > best.score) {
      best = { item, score };
    }
  }

  // Thresholds
  if (best.score >= 0.95) return { matchedItem: best.item, confidence: 'exact' };
  if (best.score >= 0.75) return { matchedItem: best.item, confidence: 'high' };
  if (best.score >= 0.5) return { matchedItem: best.item, confidence: 'medium' };
  if (best.score >= 0.3) return { matchedItem: best.item, confidence: 'low' };
  return { matchedItem: null, confidence: 'none' };
}

/**
 * Compute a fuzzy match score between two strings.
 * Returns 0–1 where 1 is identical.
 */
function computeMatchScore(a: string, b: string): number {
  const A = a.toLowerCase().trim();
  const B = b.toLowerCase().trim();
  if (A === B) return 1;

  // Exact substring
  if (A.includes(B) || B.includes(A)) return 0.92;

  // Token Jaccard
  const tokensA = new Set(A.split(/[\s\-–—/]+/).filter((t) => t.length > 2));
  const tokensB = new Set(B.split(/[\s\-–—/]+/).filter((t) => t.length > 2));
  if (tokensA.size === 0 || tokensB.size === 0) return 0;

  const intersection = new Set([...tokensA].filter((t) => tokensB.has(t)));
  const union = new Set([...tokensA, ...tokensB]);
  const jaccard = intersection.size / union.size;

  // Key word bonus
  const keyWords = ['concrete', 'slab', 'foundation', 'frame', 'framing', 'roof', 'cladding',
    'window', 'door', 'kitchen', 'bathroom', 'electrical', 'plumbing', 'deck',
    'driveway', 'landscaping', 'insulation', 'gib', 'paint', 'handover', 'consent',
    'inspection', 'completion', 'survey', 'drainage', 'services', 'fit-off'];
  let keyBonus = 0;
  for (const kw of keyWords) {
    if (A.includes(kw) && B.includes(kw)) keyBonus += 0.05;
  }
  keyBonus = Math.min(keyBonus, 0.25);

  return Math.min(1, jaccard + keyBonus);
}

export function getConfidenceColor(confidence: ImportPreviewRow['confidence']): {
  bg: string;
  text: string;
  label: string;
} {
  switch (confidence) {
    case 'exact': return { bg: '#F0FDF4', text: '#059669', label: 'Exact match' };
    case 'high': return { bg: '#ECFDF5', text: '#10B981', label: 'High confidence' };
    case 'medium': return { bg: '#FEF0E7', text: '#E8621A', label: 'Medium confidence' };
    case 'low': return { bg: '#FFF7ED', text: '#F97316', label: 'Low confidence' };
    case 'none': return { bg: '#F3F4F6', text: '#9CA3AF', label: 'No match' };
    default: return { bg: '#F3F4F6', text: '#9CA3AF', label: 'Unknown' };
  }
}

export function getConfidenceShort(confidence: ImportPreviewRow['confidence']): string {
  switch (confidence) {
    case 'exact': return 'Exact';
    case 'high': return 'High';
    case 'medium': return 'Medium';
    case 'low': return 'Low';
    case 'none': return 'New';
    default: return '-';
  }
}